<template>
  <div>
    <v-row v-if="payFrequencyId">
      <v-col cols="12" class="py-0">
        <v-switch
          class="mt-0"
          :input-value="useCustomRange"
          label="Custom Range"
          @change="handleCustomRangeToggle"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="9">
        <PayrollPeriodRangeFilter
          v-if="!useCustomRange"
          @frequency-change="handleFrequencyChange"
          @search="handlePeriodChange"
        />
        <PayrollCustomRangeFilter v-else />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="3"
        v-if="payFrequencyId"
      >
        <BusinessEntitiesDropDownList
          :clearable="true"
          :value="businessEntityId"
          :hideDetails="true"
          includeAllBusinessEntitiesOption
          @businessEntityIdChange="businessEntityIdChangeHandler"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3"
        v-if="businessEntityId && businessEntityId !== ALL_BUSINESS_ENTITIES"
      >
        <ClientsDropDownList
          :clearable="true"
          :id="clientId"
          :businessEntityId="businessEntityId"
          :hideDetails="true"
          @clientIdChange="clientIdChangeHandler"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3"
        v-if="payFrequencyId"
      >
        <v-switch
          v-model="showDetails"
          label="Show Details"
        />
      </v-col>
    </v-row>
    <div
      v-if="useCustomRange && searchValidation.error"
      class="error--text mb-2 text-right"
    >
      {{ searchValidation.error }}
    </div>
    <v-row v-if="useCustomRange" justify="end">
      <v-col cols="12" md="6">
        <FilterButtons
          @search="search"
          @clear="handleClearClick"
        ></FilterButtons>
      </v-col>
    </v-row>
    <FilterButtonsSmall
      v-if="useCustomRange"
      @search="search"
      @clear="handleClearClick"
    ></FilterButtonsSmall>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities/'
import { ClientsDropDownList } from '@/components/clients'
import { ALL_BUSINESS_ENTITIES } from '@/lib/constants/businessEntities'
import { IN_PROGRESS } from '@/lib/constants/actionStatuses'
import PayrollCustomRangeFilter from './PayrollCustomRangeFilter'
import PayrollPeriodRangeFilter from './PayrollPeriodRangeFilter'
import {
  PAYROLL_SEARCH_MODE,
  PAYROLL_FILTER,
  FILTER_CHANGE_EFFECT,
  buildAppliedFilters,
  effectOfFilterChange,
  validatePendingSearch
} from './payrollSearchPolicy'

export default {
  components: {
    BusinessEntitiesDropDownList,
    ClientsDropDownList,
    PayrollCustomRangeFilter,
    PayrollPeriodRangeFilter,
    FilterButtons,
    FilterButtonsSmall
  },
  data () {
    return {
      ALL_BUSINESS_ENTITIES
    }
  },
  computed: {
    ...mapFields('payrollReportFilters',
      [
        'payFrequencyId',
        'dateBegin',
        'dateEnd',
        'businessEntityId',
        'clientId',
        'showDetails',
        'useCustomRange'
      ]),
    ...mapGetters('payrollReportFilters', ['pendingFilters']),
    ...mapState('payrollReport', { reportStatus: state => state.meta.status }),
    searchMode () {
      return this.useCustomRange ? PAYROLL_SEARCH_MODE.CUSTOM_RANGE : PAYROLL_SEARCH_MODE.PAY_FREQUENCY
    },
    searchValidation () {
      return validatePendingSearch(this.pendingFilters)
    }
  },
  methods: {
    ...mapActions('payrollReportFilters', ['applyPayrollReportFilters', 'clearPayrollReportFilters']),
    ...mapActions('payrollReport', ['getPayrollReportWithFilters']),
    // The single search entry point. Committing the filters before the request is what keeps the
    // rows, the printed criteria, the export and the URL describing the search that produced them.
    search () {
      if (this.reportStatus === IN_PROGRESS || !this.searchValidation.valid) return
      this.applyFilters()
      return this.getPayrollReportWithFilters()
    },
    handleClearClick () {
      this.clearPayrollReportFilters()
      this.$emit('clear')
    },
    applyFilters () {
      this.applyPayrollReportFilters(buildAppliedFilters(this.pendingFilters))
    },
    handleFilterChange (filter) {
      const effect = effectOfFilterChange(this.searchMode, filter)

      if (effect === FILTER_CHANGE_EFFECT.SEARCH) {
        return this.search()
      }
      if (effect === FILTER_CHANGE_EFFECT.APPLY) {
        this.applyFilters()
      }
    },
    handleCustomRangeToggle (isCustomRange) {
      this.useCustomRange = Boolean(isCustomRange)
      // Custom Range replaces the period picker rather than layering on top of it, so leaving the
      // custom dates behind on the way out would strand the picker with a range it cannot match to
      // a period. Cleared, the picker re-derives the current period on mount and searches for it.
      // Entering Custom Range keeps whatever dates are already selected as a starting point.
      if (!isCustomRange) {
        this.dateBegin = null
        this.dateEnd = null
      }
    },
    handleFrequencyChange () {
      this.emptyClientFilter()
    },
    handlePeriodChange () {
      this.emptyClientFilter()
      return this.handleFilterChange(PAYROLL_FILTER.PERIOD)
    },
    emptyClientFilter () {
      this.clientId = null
    },
    businessEntityIdChangeHandler (newValue) {
      this.emptyClientFilter()
      this.businessEntityId = newValue
      return this.handleFilterChange(PAYROLL_FILTER.BUSINESS_ENTITY)
    },
    clientIdChangeHandler (newValue) {
      this.clientId = newValue
      return this.handleFilterChange(PAYROLL_FILTER.CLIENT)
    }
  }
}
</script>
